import { CategoryType } from '../types/devices';

const CACHED_CATEGORY_KEY = 'cachedCategory';

export type CachedCategoryType = {
  category: CategoryType;
  lastUpdatedDate: number; // timestamp in ms
  userEmail: string;
};

type CachedCategoryListener = () => void;

// Module-scoped singleton listeners array for testability
const cachedCategoryListeners: CachedCategoryListener[] = [];

export function getCachedCategoryListeners(): CachedCategoryListener[] {
  return cachedCategoryListeners;
}

export function initializeCachedCategory(userEmail: string): void {
  const cacheKey = CACHED_CATEGORY_KEY;
  let categories: CachedCategoryType[] = [];

  const stored = localStorage.getItem(cacheKey);
  if (stored) {
    try {
      categories = JSON.parse(stored) as CachedCategoryType[];

      const userCategories = categories.filter(
        (entry) => entry.userEmail === userEmail
      );
      const hasHomeOffice = userCategories.some(
        (entry) => entry.category === CategoryType.HOME_AND_OFFICE_PRINTER
      );
      const hasPersonalSystems = userCategories.some(
        (entry) => entry.category === CategoryType.PERSONAL_SYSTEMS
      );

      const categoriesToAdd = [];
      if (!hasHomeOffice) {
        categoriesToAdd.push({
          category: CategoryType.HOME_AND_OFFICE_PRINTER,
          lastUpdatedDate: Date.now(),
          userEmail
        });
      }
      if (!hasPersonalSystems) {
        categoriesToAdd.push({
          category: CategoryType.PERSONAL_SYSTEMS,
          lastUpdatedDate: Date.now(),
          userEmail
        });
      }

      if (categoriesToAdd.length > 0) {
        categories.push(...categoriesToAdd);
        localStorage.setItem(cacheKey, JSON.stringify(categories));
        notifyCachedCategoryChange();
      }
    } catch {
      categories = [
        {
          category: CategoryType.HOME_AND_OFFICE_PRINTER,
          lastUpdatedDate: Date.now(),
          userEmail
        },
        {
          category: CategoryType.PERSONAL_SYSTEMS,
          lastUpdatedDate: Date.now(),
          userEmail
        }
      ];
      localStorage.setItem(cacheKey, JSON.stringify(categories));
      notifyCachedCategoryChange();
    }
  } else {
    categories = [
      {
        category: CategoryType.HOME_AND_OFFICE_PRINTER,
        lastUpdatedDate: Date.now(),
        userEmail
      },
      {
        category: CategoryType.PERSONAL_SYSTEMS,
        lastUpdatedDate: Date.now(),
        userEmail
      }
    ];
    localStorage.setItem(cacheKey, JSON.stringify(categories));
    notifyCachedCategoryChange();
  }
}

export function subscribeCachedCategoryChange(
  listener: CachedCategoryListener
): () => void {
  cachedCategoryListeners.push(listener);
  return () => {
    const idx = cachedCategoryListeners.indexOf(listener);
    if (idx > -1) cachedCategoryListeners.splice(idx, 1);
  };
}

function notifyCachedCategoryChange() {
  cachedCategoryListeners.forEach((listener) => listener());
}

export function getCachedCategory(userEmail: string): CachedCategoryType[] {
  const stored = localStorage.getItem(CACHED_CATEGORY_KEY);
  if (stored) {
    try {
      const allCategories = JSON.parse(stored) as CachedCategoryType[];
      return allCategories.filter((entry) => entry.userEmail === userEmail);
    } catch {
      // fallback to empty if parsing fails
    }
  }
  return [];
}

function setCachedCategory(
  categories: CachedCategoryType[],
  userEmail: string
): void {
  const stored = localStorage.getItem(CACHED_CATEGORY_KEY);

  let allCategories: CachedCategoryType[] = [];
  if (stored) {
    try {
      allCategories = JSON.parse(stored) as CachedCategoryType[];
      allCategories = allCategories.filter(
        (entry) => entry.userEmail !== userEmail
      );
      allCategories = [...allCategories, ...categories];
    } catch {
      allCategories = categories;
    }
  } else {
    allCategories = categories;
  }

  localStorage.setItem(CACHED_CATEGORY_KEY, JSON.stringify(allCategories));
  notifyCachedCategoryChange();
}

/**
 * Utility to update or add a category entry with a new lastUpdatedDate.
 */
export function updateCachedCategory(
  category: CategoryType,
  userEmail: string,
  lastUpdatedDate: number | undefined = undefined
): void {
  const categories = getCachedCategory(userEmail);
  const idx = categories.findIndex((entry) => entry.category === category);
  if (idx > -1) {
    categories[idx].lastUpdatedDate = lastUpdatedDate;
    categories[idx].userEmail = userEmail;
  } else {
    categories.push({ category, lastUpdatedDate, userEmail });
  }
  setCachedCategory(categories, userEmail);
}

/**
 * Utility to check if a category is cached.
 */
export function isCachedCategory(
  category: CategoryType,
  userEmail: string
): boolean {
  return getCachedCategory(userEmail).some(
    (entry) => entry.category === category && entry.userEmail === userEmail
  );
}
